import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { events, categories } from '@/mocks/events';

const navItems = [
  { to: '/', label: '首页' },
  { to: '/events', label: '事件时间轴' },
  { to: '/about', label: '关于我们' },
];

function BrandMark({ size = 'md' }: { size?: 'sm' | 'md' }) {
  return (
    <Link to="/" className="group flex items-center gap-2.5">
      <span
        className={`${size === 'sm' ? 'h-7 w-7 text-base' : 'h-9 w-9 text-lg'} flex items-center justify-center rounded-lg bg-primary-500 font-heading text-background-50 shadow-sm transition-transform group-hover:-rotate-6`}
      >
        瓜
      </span>
      <span className="flex flex-col leading-none">
        <span className={`font-heading font-semibold tracking-wide text-foreground-950 ${size === 'sm' ? 'text-lg' : 'text-xl'}`}>
          瓜迹
        </span>
        <span className={`text-[10px] tracking-[0.18em] text-foreground-400 ${size === 'sm' ? 'hidden' : ''}`}>热点事件时间轴</span>
      </span>
    </Link>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const ticker = events.slice(0, 6);

  return (
    <header className="sticky top-0 z-50">
      {/* 顶部追更跑马灯 */}
      <div className="hidden overflow-hidden border-b border-background-200 bg-primary-950 text-primary-100 md:block">
        <div className="flex items-center">
          <span className="z-10 flex h-8 shrink-0 items-center gap-1.5 bg-primary-600 px-3 text-xs font-semibold text-background-50">
            <i className="ri-flashlight-fill text-[11px]" /> 追更热点
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="marquee-track flex w-max items-center gap-10 whitespace-nowrap py-2 pl-4 text-xs">
              {[...ticker, ...ticker].map((ev, i) => (
                <Link key={`${ev.id}-${i}`} to={`/event/${ev.id}`} className="flex items-center gap-2 transition-colors hover:text-accent-300">
                  <span className="h-1 w-1 rounded-full bg-accent-400" />
                  {ev.title}
                  <span className="text-primary-400/70 tabular-nums">更新 {ev.updated.slice(5).replace('-', '/')}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 主导航 */}
      <nav className="border-b border-background-200/80 bg-background-50/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
          <BrandMark />

          <div className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `relative rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                    isActive ? 'text-primary-700' : 'text-foreground-700 hover:text-primary-700'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    {isActive && <span className="absolute inset-x-4 -bottom-[13px] h-0.5 rounded-full bg-primary-500" />}
                  </>
                )}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/events"
              className="hidden items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
            >
              <i className="ri-time-line text-base" /> 开始吃瓜
            </Link>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label="打开菜单"
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-200 text-foreground-800 transition-colors hover:bg-background-100 md:hidden"
            >
              <i className={`text-xl ${open ? 'ri-close-line' : 'ri-menu-3-line'}`} />
            </button>
          </div>
        </div>

        {/* 移动端菜单 */}
        {open && (
          <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-2 md:hidden">
            <div className="flex flex-col">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `flex items-center justify-between rounded-md px-3 py-3 text-sm font-medium ${
                      isActive ? 'bg-primary-50 text-primary-700' : 'text-foreground-800 hover:bg-background-100'
                    }`
                  }
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line" />
                </NavLink>
              ))}
            </div>
            <div className="mt-3 border-t border-background-200 pt-3">
              <p className="px-3 text-xs font-medium text-foreground-400">快速栏目</p>
              <div className="mt-2 flex flex-wrap gap-2 px-3">
                {categories.filter((c) => c !== '全部').map((c) => (
                  <Link key={c} to={`/events?cat=${encodeURIComponent(c)}`} className="rounded-full border border-background-200 bg-background-100 px-3 py-1 text-xs text-foreground-700 hover:border-primary-200 hover:text-primary-700">
                    {c}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}